import Avatar from '@/common/components/atom/avatar';
import ChevronIcon from '@/common/components/icons/chevron';
import classNames from '@/common/utils/classNames';
import type { ReactNode } from 'react';
import { formatReleaseAt } from '../functions/formatReleaseAt';

/** Inline queue illustration (see ./scene/scenes.ts). Decorative: the text around it says the same. */
export const Scene = ({ svg, className }: { svg: string; className?: string }) => (
  <div aria-hidden className={classNames('[&>svg]:block [&>svg]:w-full [&>svg]:h-auto', className)} dangerouslySetInnerHTML={{ __html: svg }} />
);

/** «فردا ساعت ۰۷:۴۵» / «شنبه ساعت ۰۸:۰۰» — next release of new appointments. */
export const releaseText = (releaseAt?: string | null) => {
  const release = formatReleaseAt(releaseAt);
  if (!release) return null;
  const day = release.relative === 'امروز' || release.relative === 'فردا' ? release.relative : release.weekday;
  return `${day} ساعت ${release.clock}`;
};

/** Label under a queue picture, on the booth side. */
export const SceneCaption = () => <span className="-mt-1 text-xs font-medium text-right text-slate-500">نوبت آزاد بعدی</span>;

export const DoctorAvatar = ({ src, name, size }: { src?: string; name: string; size: number }) => (
  <Avatar
    src={src}
    name={name}
    width={size}
    height={size}
    className="rounded-full border border-solid border-slate-200 bg-slate-50 shrink-0 object-cover"
  />
);

const chipTones = {
  green: 'bg-green-50 text-green-700',
  blue: 'bg-[#EFF3FF] text-[#2A4FD9]',
  gray: 'bg-slate-100 text-slate-600',
  orange: 'bg-orange-50 text-orange-700',
} as const;

export const StatusChip = ({ children, tone, dot }: { children: ReactNode; tone: keyof typeof chipTones; dot?: boolean }) => (
  <span className={classNames('inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap', chipTones[tone])}>
    {dot && <span className="w-1.5 h-1.5 rounded-full bg-green-600" />}
    {children}
  </span>
);

/** Grey key/value block of the appointment cards. */
export const InfoRows = ({ rows }: { rows: { label: string; value: ReactNode }[] }) => (
  <div className="flex flex-col gap-3.5 px-4 py-3 text-sm rounded-md bg-[#F8FAFB]">
    {rows.map(row => (
      <div key={row.label} className="flex items-center justify-between gap-3">
        <span className="text-slate-600 whitespace-nowrap">{row.label}</span>
        <span className="font-bold text-left">{row.value}</span>
      </div>
    ))}
  </div>
);

/** Small framed note (info = blue, warning = amber, plain = grey). */
export const Note = ({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'warning' | 'plain' }) => (
  <div
    className={classNames('flex items-start gap-2.5 p-3 text-[13px] font-medium leading-[1.8] border border-solid rounded-lg', {
      'bg-[#EFF6FF] border-[#93C5FD] text-[#155E75]': tone === 'info',
      'bg-amber-50 border-amber-300 text-amber-800': tone === 'warning',
      'bg-[#F8FAFB] border-transparent text-slate-600': tone === 'plain',
    })}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden className="mt-0.5 shrink-0">
      <circle cx="12" cy="12" r="9" />
      {tone === 'warning' ? <path d="M12 7.5v5.5M12 16.5h.01" /> : <path d="M12 11v5M12 8h.01" />}
    </svg>
    <span>{children}</span>
  </div>
);

/** Primary action with the official left chevron (h-48, radius 8). */
export const PrimaryButton = ({
  children,
  onClick,
  chevron = true,
  loading,
  disabled,
  testId,
}: {
  children: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  loading?: boolean;
  disabled?: boolean;
  testId?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading || disabled}
    data-testid={testId}
    className="flex items-center justify-center w-full h-12 gap-2 text-sm font-bold text-white border border-solid rounded-lg bg-primary border-primary disabled:opacity-70"
  >
    <span>{loading ? 'لطفاً صبر کنید…' : children}</span>
    {chevron && !loading && <ChevronIcon dir="left" />}
  </button>
);

export const OutlineButton = ({
  children,
  onClick,
  danger,
  testId,
}: {
  children: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  testId?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={classNames('flex items-center justify-center w-full h-12 gap-1.5 text-sm font-bold bg-white border border-solid rounded-lg', {
      'border-red-500 text-red-500': danger,
      'border-primary/40 text-primary': !danger,
    })}
  >
    {children}
  </button>
);
